import { Injectable, NgZone, OnDestroy } from '@angular/core';
import { HttpBackend, HttpClient } from '@angular/common/http';
import { NavigationEnd, Router } from '@angular/router';
import { Observable, Subject, Subscription } from 'rxjs';
import { AGENT_FOLLOW_INFO, AgentFollowService } from './agent-follow.service';
import { DpgfActivityFeed, FeedApi } from './agent-follow-feed';

// DPGF activity feed (docs/mcp/06-FRONT.md §8.2, R12): root service that plugs the pure DpgfActivityFeed into the HTTP
// client, the router, the document and the follow engine (changes$, highlights, flush, « Voir »). Created by the card
// « Claude modifie Aglo » (agent-follow-card.component, mounted by the activity bubble on every architect page).
// The client is built on HttpBackend, like the Connexions IA section: it bypasses the global interceptor, whose 404 and 403
// redirections would leave the page when the API has no such route (shared demo: the last #deploy wins); the feed then
// stops silently. Timers run outside Angular: a read with nothing new triggers no change detection.
@Injectable({ providedIn: 'root' })
export class AgentFollowFeedService implements OnDestroy {
  readonly feed: DpgfActivityFeed;
  readonly viewChanged$: Observable<void>;
  private readonly view = new Subject<void>();
  private readonly http: HttpClient;
  private readonly subscription: Subscription;
  private readonly visibility = () => this.feed.sync();
  private readonly agentBase = '/api/v1/ai/agent/v2';

  constructor(backend: HttpBackend, private router: Router, private zone: NgZone, follow: AgentFollowService) {
    this.http = new HttpClient(backend);
    this.viewChanged$ = this.view.asObservable();
    const api: FeedApi = {
      // The cursor is opaque (digits and an id): no token, no personal data in the address.
      feed: (dpgfId, cursor) => this.http.get<unknown>(`${this.agentBase}/activity?dpgfId=${encodeURIComponent(dpgfId)}${cursor ? `&after=${encodeURIComponent(cursor)}` : ''}`).toPromise(),
      change: (dpgfId, changeSetId) => this.http.get<unknown>(`${this.agentBase}/activity?dpgfId=${encodeURIComponent(dpgfId)}&changeSetId=${encodeURIComponent(changeSetId)}`).toPromise(),
      undo: changeSetId => this.http.post<unknown>(`${this.agentBase}/changesets/${encodeURIComponent(changeSetId)}/undo`, {}).toPromise(),
      pause: grantId => this.http.patch<unknown>(`/api/v1/mcp/connections/${encodeURIComponent(grantId)}`, { paused: true }).toPromise()
    };
    this.feed = new DpgfActivityFeed({
      api,
      now: () => Date.now(),
      timers: { setTimeout: (callback, ms) => this.zone.runOutsideAngular(() => setTimeout(callback, ms)), clearTimeout: handle => clearTimeout(handle as any) },
      visible: () => typeof document === 'undefined' || document.visibilityState !== 'hidden',
      url: () => this.router.url,
      publish: change => follow.engine.feedChange(change),
      highlight: (dpgfId, lotIds, postIds) => follow.engine.highlight(dpgfId, lotIds, postIds),
      isShown: intent => follow.engine.isShown(intent),
      pageBlock: () => follow.engine.pageBlock(),
      open: intent => follow.open(intent),
      flush: () => follow.flushPages(),
      go: commands => { void this.zone.run(() => this.router.navigate(commands)); },
      replaceQuery: params => { void this.zone.run(() => this.router.navigate([], { queryParams: params, queryParamsHandling: 'merge', replaceUrl: true, info: AGENT_FOLLOW_INFO })); },
      changed: () => this.view.next()
    });
    this.subscription = this.router.events.subscribe(event => { if (event instanceof NavigationEnd) this.feed.sync(); });
    if (typeof document !== 'undefined') this.zone.runOutsideAngular(() => document.addEventListener('visibilitychange', this.visibility));
    this.feed.sync();
  }

  ngOnDestroy() {
    this.subscription.unsubscribe(); this.feed.dispose();
    if (typeof document !== 'undefined') document.removeEventListener('visibilitychange', this.visibility);
  }
}
